import { useLanguage } from '@/i18n/LanguageContext';
import { eventsPageQuery, publicEventsApi } from '@/features/publicsite/events/api/publicEventsApi';
import { eventFilters } from '@/features/publicsite/events/eventFilters';
import { calendarOrder } from '@/features/publicsite/events/eventOrder';
import { todayIso } from '@/features/common/calendar';
import type { EventCalendarPage } from '@/features/publicsite/types/events/EventCalendarPage';
import { useServerPage } from '@/services/useServerPage';

/**
 * One page of /evenimente for the filters in `params`, and whether what is on screen answers them —
 * useServerPage. A failed request raises `failed` and leaves the page alone, so the calendar can
 * say the request failed — resolving to an empty page instead told the reader there are no events
 * in the period they chose, which is a different sentence and a false one.
 *
 * Until the answer to the current question arrives the months on screen belong to the previous
 * one: they stay, but the count and the empty state wait (`fresh`).
 */
export function useEventCalendarPage(params: URLSearchParams, page: number): EventCalendarPage {
  const { language } = useLanguage();
  const filters = eventFilters(params);
  // The request as a string, so the page is asked again when a filter changes and not when the URL
  // is rewritten with the same filters in another order.
  const request = eventsPageQuery(filters, page, calendarOrder(filters, todayIso()));
  const key = JSON.stringify([request, language]);
  const answer = useServerPage(() => publicEventsApi.page(request), key);
  return {
    data: answer.data,
    fresh: answer.answeredKey === key,
    loading: answer.loading,
    failed: answer.failed,
  };
}
